import type { FieldDef, Json, RecordData } from "../core";
import { formatDisplay } from "../core";

export const recordId = (rec: RecordData): string => String(rec.id ?? rec.record_uuid);

// Read the value of a field from a record, routing custom properties through
// their JSONB bag. A synthesized property FieldDef (`isProperty`) stores its
// value at `record[bag][key]`, NOT at the top-level wire name `"<bag>:<key>"`.
// Every cell renderer (list / kanban) reads through this so property columns
// show their value instead of blank — mirroring the form's FormPropertyRow.
export function readFieldValue(
    record: RecordData,
    field: FieldDef | undefined,
    name: string,
): Json | undefined {
    if (field?.isProperty && field.bag) {
        const raw = record[field.bag];
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            return (raw as Record<string, Json>)[field.propertyKey ?? ""] ?? null;
        }
        return null;
    }
    return record[name];
}

// Route a write back to its storage location. For a custom property the new
// value is merged into the bag sub-object and the WHOLE bag is written under
// its column name (the save path persists the JSONB column); for a normal
// field the value is written under its own name. Used by inline-edit cells.
export function writeFieldValue(
    record: RecordData,
    field: FieldDef | undefined,
    name: string,
    value: Json,
    onWriteField: (fieldName: string, val: Json) => void,
): void {
    if (field?.isProperty && field.bag) {
        const raw = record[field.bag];
        const current =
            raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, Json>) : {};
        onWriteField(field.bag, { ...current, [field.propertyKey ?? ""]: value });
        return;
    }
    onWriteField(name, value);
}

// A reference / m2m chip arrives as { id, display_name [, data] }. Pull its
// label. A per-field `displayFormat` (XML `display_format="…"` override, e.g.
// "{name}" to drop a "[CODE] " prefix) re-renders the label from the chip's
// `data` payload (the target's display_data_fields, shipped by the backend
// reference resolvers); falls back to the server-rendered display_name when
// no format is given, the chip carries no data, or the template renders empty.
export function refLabel(value: unknown, displayFormat?: string | null): string {
    if (value && typeof value === "object" && "display_name" in value) {
        const chip = value as { display_name?: unknown; data?: unknown };
        if (displayFormat && chip.data && typeof chip.data === "object") {
            const rendered = formatDisplay(displayFormat, chip.data as Record<string, unknown>).trim();
            if (rendered) return rendered;
        }
        return String(chip.display_name ?? "");
    }
    return String(value);
}

// An Enum value arrives as the rich read shape `[key, label]`; a bare key may
// still appear (legacy / after an inline edit). `isEnumPair` distinguishes it
// from a chip list (whose members are objects, not strings).
export function isEnumPair(value: unknown): value is [string, string] {
    return Array.isArray(value) && value.length === 2
        && typeof value[0] === "string" && typeof value[1] === "string";
}

// Stored key of an Enum value — index 0 of `[key, label]`, else the value
// itself. Used for edit / dirty / write / statusbar-current logic.
export function enumKey(value: unknown): unknown {
    return isEnumPair(value) ? value[0] : value;
}

// Display label of an Enum value — index 1 of `[key, label]`, falling back to
// the field's selection map for a bare key.
export function enumLabel(
    value: unknown,
    selection?: ReadonlyArray<readonly [string, string]>,
): string {
    if (isEnumPair(value)) return value[1] || value[0];
    const found = selection?.find(([k]) => k === String(value));
    return found ? found[1] : String(value ?? "");
}

// Group label for a value under a group-by field.
export function groupKeyOf(value: Json | undefined): string {
    if (value == null || value === "") return "Undefined";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (isEnumPair(value)) return value[1] || value[0];
    if (Array.isArray(value)) return value.length ? value.map((v) => refLabel(v)).join(", ") : "Undefined";
    if (typeof value === "object") return refLabel(value);
    return String(value);
}
